import { useState } from "react";
import { useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Sparkles, Lock, LogIn } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface SignupModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  brandName: string;
  onSuccess?: () => void;
  onBeforeOAuthRedirect?: () => void;
}

export const SignupModal = ({ open, onOpenChange, brandName, onSuccess, onBeforeOAuthRedirect }: SignupModalProps) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isLoginMode, setIsLoginMode] = useState(false);
  const location = useLocation();

  const handleGoogleLogin = async () => {
    setIsLoading(true);
    try {
      // CRITICAL: Save return path BEFORE OAuth redirect
      const currentPath = location.pathname + location.search;
      localStorage.setItem('clicklogos_auth_return_path', currentPath);
      console.log('[GoogleAuth] Saved return path:', currentPath);
      
      // Save wizard state BEFORE OAuth redirect
      if (onBeforeOAuthRedirect) {
        console.log('[GoogleAuth] Saving wizard state before redirect...');
        onBeforeOAuthRedirect();
      }
      
      const redirectUrl = `${window.location.origin}/auth/callback`;
      console.log('[GoogleAuth] Initiating OAuth with redirectTo:', redirectUrl);
      
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: redirectUrl,
          queryParams: {
            access_type: 'offline',
            prompt: 'consent',
          },
        },
      });
      
      console.log('[GoogleAuth] OAuth response:', { data, error });
      
      if (error) {
        console.error('[GoogleAuth] OAuth error:', error);
        toast.error("Erro ao conectar com Google: " + error.message);
      }
    } catch (error) {
      console.error("[GoogleAuth] Exception:", error);
      toast.error("Erro ao conectar com Google");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      if (isLoginMode) {
        // Login flow
        const { error } = await supabase.auth.signInWithPassword({
          email,
          password,
        });

        if (error) {
          if (error.message.includes("Invalid login credentials")) {
            toast.error("E-mail ou senha incorretos");
          } else {
            toast.error(error.message);
          }
          setIsLoading(false);
          return;
        }

        toast.success("Login realizado com sucesso!");
      } else {
        // Signup flow
        const { error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            emailRedirectTo: `${window.location.origin}/`,
          },
        });

        if (error) {
          if (error.message.includes("already registered")) {
            toast.error("Este e-mail já está cadastrado. Faça login.");
            setIsLoginMode(true);
          } else {
            toast.error(error.message);
          }
          setIsLoading(false);
          return;
        }

        toast.success("Conta criada com sucesso!");
      }

      // Success - close modal and trigger callback
      onOpenChange(false);
      onSuccess?.();
    } catch (error) {
      console.error("Auth error:", error);
      toast.error("Erro ao processar. Tente novamente.");
    } finally {
      setIsLoading(false);
    }
  };

  const toggleMode = () => {
    setIsLoginMode(!isLoginMode);
    setEmail("");
    setPassword("");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader className="text-center space-y-3">
          <div className="mx-auto w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
            {isLoginMode ? (
              <LogIn className="w-6 h-6 text-primary" />
            ) : (
              <Lock className="w-6 h-6 text-primary" />
            )}
          </div>
          <DialogTitle className="text-xl">
            {isLoginMode ? "Bem-vindo de volta!" : `Salve sua marca: ${brandName}`}
          </DialogTitle>
          <DialogDescription>
            {isLoginMode
              ? "Faça login para acessar suas marcas e continuar."
              : "Crie uma conta gratuita para baixar seus arquivos e salvar sua marca."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          {/* Google OAuth Button */}
          <Button
            type="button"
            variant="outline"
            className="w-full gap-2"
            size="lg"
            onClick={handleGoogleLogin}
            disabled={isLoading}
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continuar com Google
          </Button>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-background px-2 text-muted-foreground">
                Ou continue com e-mail
              </span>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email">E-mail</Label>
            <Input
              id="email"
              type="email"
              placeholder="seu@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password">Senha</Label>
            <Input
              id="password"
              type="password"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={6}
            />
          </div>

          <Button type="submit" className="w-full" size="lg" disabled={isLoading}>
            {isLoading ? (
              isLoginMode ? "Entrando..." : "Criando conta..."
            ) : (
              <>
                {isLoginMode ? (
                  <>
                    <LogIn className="w-4 h-4 mr-2" />
                    Entrar
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    Criar Conta e Baixar
                  </>
                )}
              </>
            )}
          </Button>

          <p className="text-sm text-center text-muted-foreground">
            {isLoginMode ? "Não tem uma conta? " : "Já tem uma conta? "}
            <button
              type="button"
              onClick={toggleMode}
              className="text-primary underline hover:no-underline font-medium"
            >
              {isLoginMode ? "Criar uma" : "Faça Login"}
            </button>
          </p>

          {!isLoginMode && (
            <p className="text-xs text-center text-muted-foreground">
              Ao criar sua conta, você concorda com nossos{" "}
              <a href="/termos-de-uso" className="underline hover:text-foreground">
                Termos de Uso
              </a>{" "}
              e{" "}
              <a href="/politica-privacidade" className="underline hover:text-foreground">
                Política de Privacidade
              </a>
              .
            </p>
          )}
        </form>
      </DialogContent>
    </Dialog>
  );
};
